import {
  Box,
  Button,
  Flex,
  Icon,
  InlineStack,
  Panel,
  PanelActions,
  PanelHeader,
  PanelInset,
  PanelSection,
  PanelTitle,
  Portal,
  ProgressBar,
  Spinner,
  Text,
} from '@customerio/pluma-components/react';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useAnalytics } from '@/services/analytics';

import * as styles from './asset-upload-popup.css';
import { isOversizedImage, type AssetProcessedFile } from './utils';

type AssetUploadPopupProps = {
  files: AssetProcessedFile[];
  hideProgressBar?: boolean;
  onClose: () => void;
  onOptimize: (file: AssetProcessedFile) => void;
};

export function AssetUploadPopup({ files, hideProgressBar = false, onClose, onOptimize }: AssetUploadPopupProps) {
  const { track } = useAnalytics();
  const [isExpanded, setIsExpanded] = useState(true);
  const [mouseInside, setMouseInside] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const uploadItemsTotal = files.length;
  const uploadItemsSucceeded = files.filter((f) => f.state === 'SUCCESS').length;
  const uploadItemsErrored = files.filter((f) => f.state === 'FAILED').length;
  const uploadItemsProcessed = uploadItemsSucceeded + uploadItemsErrored;
  const uploadItemsToProcess = uploadItemsTotal - uploadItemsProcessed;
  const isUploading = uploadItemsToProcess > 0;

  const uploadProgressBarPercentage = Math.round((uploadItemsProcessed / uploadItemsTotal) * 100);

  const handleExpandCollapse = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) setMouseInside(false);
  };

  useEffect(
    function autoScrollToUploadingItem() {
      if (!mouseInside && listRef.current != null) {
        const uploadingElement = listRef.current.querySelector('[data-uploading="true"]');
        if (uploadingElement != null) {
          uploadingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },
    [files, mouseInside],
  );

  if (uploadItemsTotal === 0) return null;

  return (
    <Portal>
      <Box className={styles.popupContainer} data-test-id="asset-library-upload-queue-popup">
        <Box className={styles.popupPanel}>
          <Panel>
            <PanelHeader p="150">
              <PanelTitle>Uploads</PanelTitle>
              <PanelActions>
                {uploadItemsTotal > 1 ? (
                  <>
                    <Button
                      variant="subtle"
                      icon={isExpanded ? 'chevron-down' : 'chevron-up'}
                      isIconOnly
                      aria-label={isExpanded ? 'Minimize' : 'Expand'}
                      onClick={handleExpandCollapse}
                      size="sm"
                    />
                    {isUploading ? <Spinner /> : null}
                  </>
                ) : null}
                {!isUploading ? (
                  <Button variant="subtle" icon="close" isIconOnly aria-label="Close" onClick={onClose} size="sm" />
                ) : null}
              </PanelActions>
            </PanelHeader>
            <PanelSection py="100" px="150">
              {uploadItemsTotal > 1 ? (
                <Text size="s">
                  {uploadItemsSucceeded} of {uploadItemsTotal} uploaded
                  {uploadItemsErrored > 0 ? (
                    <>
                      ,{' '}
                      <Text as="span" color="critical">
                        {uploadItemsErrored} failed
                      </Text>
                    </>
                  ) : null}
                </Text>
              ) : null}
            </PanelSection>
            <PanelInset>
              {isExpanded ? (
                <Box
                  ref={listRef}
                  overflowY="auto"
                  px="150"
                  className={styles.fileList}
                  onMouseEnter={() => setMouseInside(true)}
                  onMouseLeave={() => setMouseInside(false)}
                >
                  {files.map((file, index) => (
                    <Box
                      key={file.name}
                      py="100"
                      className={clsx({
                        [styles.fileItemBorder]: index < files.length - 1,
                      })}
                      data-uploading={file.state === 'UPLOADING'}
                    >
                      <InlineStack alignBlock="center" alignInline="space-between" gap="100">
                        <Box className={styles.fileName}>
                          <Text size="s">{file.name}</Text>
                        </Box>
                        <Box className={styles.uploadIconStatus}>
                          {file.state === 'SUCCESS' ? (
                            <Icon name="check-circle" color="success" size="md" aria-label="Success" />
                          ) : null}
                          {file.state === 'FAILED' ? (
                            <Icon name="error" color="critical" size="md" aria-label="Error" />
                          ) : null}
                          {file.state === 'UPLOADING' ? <Spinner aria-label="Uploading" /> : null}
                          {file.state === 'PENDING' ? (
                            <Icon name="more" color="subtle" size="md" aria-label="Pending" />
                          ) : null}
                        </Box>
                      </InlineStack>
                      {file.state === 'FAILED' && file.reason != null ? (
                        <Box mt="050">
                          <Flex p="100" className={styles.errorBackground}>
                            <Text size="xxs" color="critical">
                              {file.reason}
                            </Text>
                          </Flex>
                          {isOversizedImage(file) ? (
                            <Box mt="100">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  onOptimize?.(file);
                                  track('assetOptimizer', 'tryOptimizing', {
                                    'Builder Type': 'design-studio',
                                  });
                                }}
                              >
                                Try optimizing
                              </Button>
                            </Box>
                          ) : null}
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              ) : null}
              {uploadItemsTotal > 1 ? (
                <Box
                  className={clsx(
                    styles.progressBarContainer,
                    hideProgressBar ? styles.progressBarHidden : styles.progressBarVisible,
                  )}
                >
                  <ProgressBar
                    size="sm"
                    value={uploadProgressBarPercentage}
                    aria-label={`${uploadProgressBarPercentage}% Complete`}
                  />
                </Box>
              ) : null}
            </PanelInset>
          </Panel>
        </Box>
      </Box>
    </Portal>
  );
}
